'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import * as Sentry from '@sentry/browser';

import { EVENT_NAMES, EVENT_PREFIX } from '@/analytics/events';
import { sendEvent } from '@/components/GoogleTagServerSide';
import { safeLocalStorage } from '@/lib/safeLocalStorage';
import { posthog } from '@/posthog';

export type EventValueType = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];
export type EventParamsType = { [key: string]: string | undefined | null };
export type EventProviderType = 'posthog' | 'ss-gtm' | 'gtm';

export function trackUserBucketedEvent(
  variant: string,
  feature: string,
  flags: Record<string, string>,
) {
  const event = `user_bucketed.${feature}.${variant}`;
  const tracked = safeLocalStorage.getItem(event);

  if (!tracked) {
    safeLocalStorage.setItem(event, Date.now());
    trackEvent(event, { [feature]: variant });
    posthog.setPersonPropertiesForFlags(flags);
    posthog.setPersonProperties(flags);
  }
}

export function trackEvent(
  eventName: string,
  eventParams?: EventParamsType,
  providers: EventProviderType[] = ['ss-gtm', 'posthog'],
) {
  if (typeof window !== 'undefined') {
    // Send to local console log
    if (process.env.NODE_ENV === 'development') {
      console.log(
        'event fired',
        `'${EVENT_PREFIX}_${eventName}'`,
        eventParams,
        providers,
      );
    }

    // Send to Server-side GTM
    if (providers.includes('ss-gtm')) {
      sendEvent(`${EVENT_PREFIX}_${eventName}`, eventParams);
    }

    // Send to Client-side GTM
    if (providers.includes('gtm')) {
      sendGTMEvent({ event: eventName, ...eventParams });
    }

    // Send to Posthog
    if (providers.includes('posthog')) {
      try {
        posthog.capture(eventName, eventParams);
      } catch (error) {
        Sentry.captureException(
          new Error(`failed to send to posthog: ${error}`, { cause: error }),
        );
      }
    }
  } else {
    Sentry.captureMessage('failed trackEvent event. window undefined', {
      level: 'error',
      extra: {
        eventName: eventName,
        providers: providers,
      },
    });
  }
}
